/**
 * Top-level framework-assessment surface (P10.4).
 *
 * Two-stage navigation: list the active SAMA frameworks, click
 * one to drill into its cycles. Cycle row -> drills further into
 * the assessment grid via `CycleAssessmentGridView`.
 *
 * Admin can create a new cycle inline; only admin-licensed sessions
 * see the "+ New cycle" affordance.
 *
 * Mounted at /framework/assessments.
 */
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { useMe } from "shared/api/useMe";
import type { ControlLocation, FrameworkCycle } from "shared/api/types";
import {
  useControlLocation,
  useCreateCycle,
  useCycles,
  useFrameworks,
} from "framework/api/assessment_hooks";

import { Arrow } from "shared/components/Arrow";
function _todayPlus(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const STATUS_TONE: Record<string, string> = {
  draft: "bg-slate-100 text-slate-800 border-slate-300",
  open: "bg-emerald-50 text-emerald-900 border-emerald-300",
  closed: "bg-slate-200 text-slate-700 border-slate-400",
};

export function FrameworkAssessmentsView() {
  const { t } = useTranslation();
  const { frameworkCode } = useParams<{ frameworkCode?: string }>();
  const me = useMe();
  const frameworksQuery = useFrameworks();

  // Round 10 F1 -- global search links a control hit here carrying only
  // the control's UUID, and this screen ignored it: the reader landed
  // on a framework picker with no sign of what they had searched for.
  // Eighth round on the same dead end.
  //
  // The hub cannot resolve a UUID by itself, so it asks, and then
  // forwards to the cycle where the control is actually graded. The
  // redirect REPLACES the history entry: a reader pressing Back wants
  // the search results they came from, not a hub that will bounce them
  // forward again.
  const [searchParams] = useSearchParams();
  const controlId = searchParams.get("control");
  const controlLocation = useControlLocation(controlId);
  const navigate = useNavigate();
  const located = controlLocation.data;

  useEffect(() => {
    if (!controlId || !located) return;
    const framework = encodeURIComponent(located.framework_code);
    if (located.cycle_id) {
      navigate(
        `/framework/assessments/${framework}/cycles/${encodeURIComponent(located.cycle_id)}?control=${encodeURIComponent(controlId)}`,
        { replace: true },
      );
    } else if (located.framework_code !== frameworkCode) {
      // No cycle to open. Show the framework's own cycles panel, where
      // the explanation below sits beside the affordance that fixes it.
      navigate(
        `/framework/assessments/${framework}?control=${encodeURIComponent(controlId)}`,
        { replace: true },
      );
    }
  }, [controlId, located, frameworkCode, navigate]);

  // Round 20 Y4 -- every cycle, so each framework card can count the
  // assessments resting on evidence older than their cycle.
  const allCycles = useCycles(undefined);
  const oldByFramework: Record<string, number> = {};
  for (const c of allCycles.data ?? []) {
    oldByFramework[c.framework_code] =
      (oldByFramework[c.framework_code] ?? 0) + (c.assessments_on_old_evidence ?? 0);
  }

  return (
    <div data-testid="framework-assessments-view" className="flex flex-col gap-4">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-slate-900">
          <MarsadIcon
            name="framework"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("screens.frameworkAssessmentsView.frameworkAssessments")}
        </h1>
        <p className="text-sm text-slate-600">
          {t(
            "screens.frameworkAssessmentsView.samaAlignedFrameworkControlCataloguesPickA",
          )}
        </p>
      </header>

      {controlId && (
        <SearchedControlNotice
          controlId={controlId}
          isLoading={controlLocation.isLoading}
          located={located ?? null}
          failed={!!controlLocation.error}
        />
      )}

      {frameworksQuery.isLoading && (
        <p className="text-xs text-slate-500">
          {t("screens.frameworkAssessmentsView.loadingFrameworks")}
        </p>
      )}
      {frameworksQuery.error && (
        <div
          data-testid="frameworks-error"
          className="rounded border border-red-300 bg-red-50 p-3 text-xs text-red-800"
        >
          {t("screens.frameworkAssessmentsView.couldNotLoadFrameworks")}{" "}
          {frameworksQuery.error instanceof Error
            ? frameworksQuery.error.message
            : t("screens.frameworkAssessmentsView.unknown")}
        </div>
      )}

      {frameworksQuery.data && (
        <section
          data-testid="frameworks-list"
          data-old-evidence-total={Object.values(oldByFramework).reduce(
            (a, n) => a + n,
            0,
          )}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
        >
          {frameworksQuery.data.map((f) => {
            const active = f.code === frameworkCode;
            return (
              <Link
                key={f.code}
                to={`/framework/assessments/${encodeURIComponent(f.code)}`}
                data-testid={`framework-card-${f.code}`}
                data-active={active}
                className={
                  "rounded-md border p-3 transition-colors " +
                  (active
                    ? "border-emerald-400 bg-emerald-50"
                    : "border-slate-200 bg-white hover:border-brand-brass")
                }
              >
                <div className="text-sm font-semibold text-slate-900">{f.name}</div>
                <div className="font-mono text-[10px] text-slate-500 mt-0.5">
                  {f.code}
                </div>
                {f.description && (
                  <p className="mt-1 text-xs text-slate-600">{f.description}</p>
                )}
                {/* Round 20 Y4 -- evidence older than its cycle, counted
                    where audit starts. */}
                {(oldByFramework[f.code] ?? 0) > 0 && (
                  <p
                    data-testid={`framework-old-evidence-${f.code}`}
                    className="mt-1 text-xs font-medium text-rag-amber"
                  >
                    {t("screens.frameworkAssessmentsView.oldEvidence", {
                      count: oldByFramework[f.code] ?? 0,
                    })}
                  </p>
                )}
              </Link>
            );
          })}
        </section>
      )}

      {frameworkCode && (
        <CyclesPanel
          frameworkCode={frameworkCode}
          isAdmin={me.data?.is_admin ?? false}
        />
      )}
    </div>
  );
}

/**
 * What happened to the control the reader searched for.
 *
 * Three honest states, and the last two are the point: a control whose
 * framework has no cycle CANNOT be opened, and saying so beside the
 * "New cycle" button is a route forward. Silently rendering a picker
 * is what made this a dead end for eight rounds.
 */
function SearchedControlNotice({
  controlId,
  isLoading,
  located,
  failed,
}: {
  controlId: string;
  isLoading: boolean;
  located: ControlLocation | null;
  failed: boolean;
}) {
  const { t } = useTranslation();

  if (failed) {
    return (
      <p
        data-testid="searched-control-missing"
        className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
      >
        {t("assessmentsHub.controlNotFound")}
      </p>
    );
  }
  if (isLoading || !located) {
    return (
      <p data-testid="searched-control-loading" className="text-xs text-slate-500">
        {t("assessmentsHub.controlLocating")}
      </p>
    );
  }
  if (located.cycle_id) {
    // The redirect is already in flight; this only shows for the frame
    // between the answer arriving and the navigation landing.
    return (
      <p data-testid="searched-control-opening" className="text-xs text-slate-500">
        {t("assessmentsHub.controlOpening", {
          control: located.control_natural_id,
          cycle: located.cycle_name ?? "",
        })}
      </p>
    );
  }
  return (
    <p
      data-testid="searched-control-no-cycle"
      data-control-id={controlId}
      className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
    >
      {t("assessmentsHub.controlNoCycle", {
        control: located.control_natural_id,
        framework: located.framework_name ?? located.framework_code,
      })}
    </p>
  );
}

interface CyclesPanelProps {
  frameworkCode: string;
  isAdmin: boolean;
}

function CyclesPanel({ frameworkCode, isAdmin }: CyclesPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cyclesQuery = useCycles(frameworkCode);
  const createCycle = useCreateCycle();
  const [showCreate, setShowCreate] = useState(false);
  const defaultBody = useMemo(
    () => ({
      framework_code: frameworkCode,
      name: `${frameworkCode.toUpperCase()} ${new Date().getFullYear()}`,
      period_start: _todayPlus(-30),
      period_end: _todayPlus(335),
      due_date: _todayPlus(365),
    }),
    [frameworkCode],
  );

  const handleCreate = async (): Promise<void> => {
    try {
      const cycle = await createCycle.mutateAsync(defaultBody);
      setShowCreate(false);
      navigate(
        `/framework/assessments/${encodeURIComponent(frameworkCode)}/cycles/${encodeURIComponent(cycle.id)}`,
      );
    } catch {
      // Mutation error surfaces via createCycle.error; keep the
      // modal open so the operator can retry.
    }
  };

  return (
    <section
      data-testid="cycles-panel"
      data-framework-code={frameworkCode}
      className="space-y-3"
    >
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-semibold text-slate-900">
          {frameworkCode.toUpperCase()} {t("screens.frameworkAssessmentsView.cycles")}
        </h2>
        {isAdmin && (
          <button
            type="button"
            data-testid="cycles-create-button"
            onClick={() => setShowCreate(true)}
            className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1 text-xs font-semibold text-brand-brassDark hover:bg-brand-brass/10"
          >
            {t("screens.frameworkAssessmentsView.newCycle")}
          </button>
        )}
      </div>

      {cyclesQuery.isLoading && (
        <p className="text-xs text-slate-500">
          {t("screens.frameworkAssessmentsView.loadingCycles")}
        </p>
      )}

      {cyclesQuery.data && cyclesQuery.data.length === 0 && (
        <p
          data-testid="cycles-empty"
          className="rounded border border-slate-200 bg-slate-50 p-4 text-center text-xs text-slate-600"
        >
          {t("screens.frameworkAssessmentsView.noCyclesYetForThisFramework")}{" "}
          {isAdmin && (
            <span>
              {t("screens.frameworkAssessmentsView.click")}{" "}
              <em>{t("screens.frameworkAssessmentsView.newCycle2")}</em>{" "}
              {t("screens.frameworkAssessmentsView.aboveToStartOne")}
            </span>
          )}
        </p>
      )}

      {cyclesQuery.data && cyclesQuery.data.length > 0 && (
        <ul data-testid="cycles-list" className="space-y-1">
          {cyclesQuery.data.map((c) => (
            <CycleRow key={c.id} cycle={c} frameworkCode={frameworkCode} />
          ))}
        </ul>
      )}

      {showCreate && (
        <div
          data-testid="cycles-create-modal"
          onClick={() => setShowCreate(false)}
          className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/30 p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-lg border border-slate-300 bg-white p-4 shadow-xl space-y-3"
          >
            <h3 className="text-sm font-bold text-slate-900">
              {t("screens.frameworkAssessmentsView.newCycle3")}{" "}
              {frameworkCode.toUpperCase()}
            </h3>
            <p className="text-xs text-slate-600">
              {t("screens.frameworkAssessmentsView.creatingWithDefaultWindow")}
              <span className="font-mono text-[11px] block mt-1">
                {defaultBody.period_start} <Arrow /> {defaultBody.period_end}{" "}
                {t("screens.frameworkAssessmentsView.due")} {defaultBody.due_date})
              </span>
            </p>
            {createCycle.error && (
              <p data-testid="cycles-create-error" className="text-xs text-rose-700">
                {t("screens.frameworkAssessmentsView.failed")}{" "}
                {createCycle.error instanceof Error
                  ? createCycle.error.message
                  : t("screens.frameworkAssessmentsView.unknown")}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="rounded border border-slate-300 px-3 py-1 text-xs text-slate-700"
              >
                {t("screens.frameworkAssessmentsView.cancel")}
              </button>
              <button
                type="button"
                data-testid="cycles-create-confirm"
                onClick={() => void handleCreate()}
                disabled={createCycle.isPending}
                className="rounded border border-brand-brassDark bg-brand-brass px-3 py-1 text-xs font-semibold text-brand-navy disabled:opacity-50"
              >
                {createCycle.isPending
                  ? t("screens.frameworkAssessmentsView.creating")
                  : t("screens.frameworkAssessmentsView.create")}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

interface CycleRowProps {
  cycle: FrameworkCycle;
  frameworkCode: string;
}

function CycleRow({ cycle, frameworkCode }: CycleRowProps) {
  const { t } = useTranslation();
  const tone = STATUS_TONE[cycle.status] ?? STATUS_TONE.draft;
  return (
    <li>
      <Link
        to={`/framework/assessments/${encodeURIComponent(frameworkCode)}/cycles/${encodeURIComponent(cycle.id)}`}
        data-testid="cycle-row"
        data-cycle-id={cycle.id}
        data-cycle-status={cycle.status}
        className="flex items-center justify-between rounded border border-slate-200 bg-white px-3 py-2 hover:border-brand-brass"
      >
        <div>
          <div className="text-sm font-medium text-slate-900">{cycle.name}</div>
          <div className="font-mono text-[10px] text-slate-500">
            {cycle.period_start} <Arrow /> {cycle.period_end}{" "}
            {t("screens.frameworkAssessmentsView.due2")} {cycle.due_date}
          </div>
          {(cycle.assessments_on_old_evidence ?? 0) > 0 && (
            <div
              data-testid="cycle-old-evidence"
              className="text-[11px] font-medium text-rag-amber"
            >
              {t("screens.frameworkAssessmentsView.oldEvidence", {
                count: cycle.assessments_on_old_evidence ?? 0,
              })}
            </div>
          )}
        </div>
        <span
          className={`text-[10px] font-bold uppercase tracking-wider rounded border px-2 py-0.5 ${tone}`}
        >
          {cycle.status}
        </span>
      </Link>
    </li>
  );
}
